import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'

import {
  Maybe,
  MembershipInterval,
  PatronDetailFragment,
  PaymentMethod,
  PendingPurchaseItemStatus,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { RequiredMembershipInfoBox } from '@/components/organisms'
import { Loading } from '@/components/organisms/loading'
import {
  CancelChangePlanButton,
  CancelPlanButton,
  ChangePlanButton,
  ResumePlanButton,
} from '@/components/pages/Membership/template/components'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { DateTime } from '@/core/entities/values/Date'
import { getActiveMembershipSubscriptionProductPlan } from '@/core/membership'

export * from './fragments'

export type State = {
  loading: boolean
  stoppingSubscription: boolean
  changingSubscription: boolean
  cancellingChangeSubscription: boolean
  resumingSubscription: boolean
  isMembershipPersonalInformationRequired: boolean
  memberships?: Maybe<PatronDetailFragment[]>
  diffAmountSubscription: number
}

export type Action = {
  onStopSubscription: (artistId: string) => Promise<void>
  onUpgradeSubscriptionPlan: (planId: string, artistId: string) => Promise<void>
  onDowngradeSubscriptionPlan: (
    planId: string,
    artistId: string
  ) => Promise<void>
  onCancelChangeSubscription: (artistId: string) => Promise<void>
  onResumeSubscription: (artistId: string) => Promise<void>
  onPreviewSubscriptionUpdate: (
    artistId: string,
    planId: string
  ) => Promise<void>
}

export const MembershipTemplate: Template<State, Action> = ({
  state,
  onStopSubscription,
  onUpgradeSubscriptionPlan,
  onDowngradeSubscriptionPlan,
  onCancelChangeSubscription,
  onResumeSubscription,
  onPreviewSubscriptionUpdate,
}) => {
  const { t } = useTranslation('membership')
  const { routerNames } = useRouter()

  return (
    <BaseLayout margin header={{ back: true, title: t('title') }}>
      <Head title={routerNames.MembershipManagement} />
      {state.loading && <LoadingCell />}
      {!state.loading && !state.memberships?.length && (
        <Typography variant="body2" sx={{ my: 3 }}>
          {t('emptyMembershipMessage')}
        </Typography>
      )}
      {!state.loading && !!state.memberships?.length && (
        <>
          <PlanListTitleCell />
          {state.isMembershipPersonalInformationRequired && (
            <Stack pb={5}>
              <RequiredMembershipInfoBox visibleLink />
            </Stack>
          )}
          <Stack spacing={5}>
            {state.memberships?.map(membership => (
              <MembershipCell
                key={membership.id}
                membership={membership}
                diffAmountSubscription={state.diffAmountSubscription}
                stoppingSubscription={state.stoppingSubscription}
                changingSubscription={state.changingSubscription}
                cancellingChangeSubscription={
                  state.cancellingChangeSubscription
                }
                resumingSubscription={state.resumingSubscription}
                onStopSubscription={onStopSubscription}
                onUpgradeSubscriptionPlan={onUpgradeSubscriptionPlan}
                onDowngradeSubscriptionPlan={onDowngradeSubscriptionPlan}
                onCancelChangeSubscription={onCancelChangeSubscription}
                onResumeSubscription={onResumeSubscription}
                onPreviewSubscriptionUpdate={onPreviewSubscriptionUpdate}
              />
            ))}
          </Stack>
        </>
      )}
    </BaseLayout>
  )
}

const PlanListTitleCell: Cell = () => {
  const { t } = useTranslation('membership')
  return (
    <CellFrame sx={{ py: 3 }}>
      <Typography variant="h5" color="text.secondary">
        {t('planCurrently')}
      </Typography>
    </CellFrame>
  )
}

type MembershipCellProps = {
  membership: PatronDetailFragment
  diffAmountSubscription: State['diffAmountSubscription']
  stoppingSubscription: State['stoppingSubscription']
  changingSubscription: State['changingSubscription']
  cancellingChangeSubscription: State['cancellingChangeSubscription']
  resumingSubscription: State['resumingSubscription']
  onStopSubscription: Action['onStopSubscription']
  onUpgradeSubscriptionPlan: Action['onUpgradeSubscriptionPlan']
  onDowngradeSubscriptionPlan: Action['onDowngradeSubscriptionPlan']
  onCancelChangeSubscription: Action['onCancelChangeSubscription']
  onResumeSubscription: Action['onResumeSubscription']
  onPreviewSubscriptionUpdate: Action['onPreviewSubscriptionUpdate']
}
/**
 * @description
 * メンバーシップ表示用カード
 */
const MembershipCell: Cell<MembershipCellProps> = ({
  membership,
  diffAmountSubscription,
  stoppingSubscription,
  changingSubscription,
  cancellingChangeSubscription,
  resumingSubscription,
  onStopSubscription,
  onUpgradeSubscriptionPlan,
  onDowngradeSubscriptionPlan,
  onCancelChangeSubscription,
  onResumeSubscription,
  onPreviewSubscriptionUpdate,
}) => {
  const { t } = useTranslation('membership')
  const { onClick } = useBrowserLink(
    membership?.artist?.pendingPurchasePatron?.voucherUrl ?? ''
  )
  const { routerPages, generatePath } = useRouter()

  const month = useMemo(() => {
    if (membership?.paymentMethod !== PaymentMethod.Konbini) {
      return DateTime.diff(membership?.formattedCreatedAt ?? null, 'month') || 0
    }
    return (
      Math.abs(
        Number(DateTime.diff(membership?.formattedExpiredAt ?? null, 'month'))
      ) || 0
    )
  }, [membership])

  const monthSuffix = month > 1 ? '_other' : '_one'

  const currentProduct = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      membership.artist,
      membership.subscription?.planId ?? ''
    )
  }, [membership.artist, membership.subscription?.planId])

  const downgradeProduct = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      membership.artist,
      membership.subscription?.downgradePlanId ?? ''
    )
  }, [membership.artist, membership.subscription?.downgradePlanId])

  const { downgradePlan, upgradePlan } = useMemo(() => {
    if (!currentProduct) {
      return { downgradePlan: undefined, upgradePlan: undefined }
    }
    if (currentProduct?.plan.interval === MembershipInterval.Year) {
      const downgradePlan = currentProduct.plans.find(
        plan => plan.interval === MembershipInterval.Month
      )
      return { downgradePlan, upgradePlan: undefined }
    }
    const upgradePlan = currentProduct.plans.find(
      plan => plan.interval === MembershipInterval.Year
    )
    return { downgradePlan: undefined, upgradePlan }
  }, [currentProduct])

  const membershipState = useMemo(() => {
    const pendingPurchasePatron = membership.artist?.pendingPurchasePatron
    if (pendingPurchasePatron?.metadata?.membership?.planId) {
      if (
        pendingPurchasePatron?.status ===
        PendingPurchaseItemStatus.WaitingPayment
      ) {
        return 'KONBINI_WAITING_PAYMENT'
      }

      return
    }
    if (membership.subscription?.downgradePlanId) {
      return 'CHANGED_PLAN'
    }
    if (membership.cancelAtPeriodEnd) {
      return 'RESUME'
    }
    return 'ACTIVE'
  }, [
    membership.artist?.pendingPurchasePatron,
    membership.cancelAtPeriodEnd,
    membership.subscription?.downgradePlanId,
  ])

  const isKonbini = membership.paymentMethod === PaymentMethod.Konbini

  return (
    <CellFrame>
      <Card elevation={0} sx={{ borderRadius: 2, bgcolor: 'grey.100' }}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Stack
            component={Link}
            direction="row"
            alignItems="center"
            spacing={3}
            sx={{ pb: 5, width: 'fit-content', textDecoration: 'none' }}
            href={generatePath(routerPages.ArtistTop, {
              artistId: membership.artist.id,
            })}
          >
            <Avatar
              src={membership.artist?.avatar?.smallUri ?? ''}
              sx={{ width: 44, height: 44 }}
            />
            <Typography variant="h6" sx={{ width: 'fit-content' }}>
              {membership.artist?.displayName}
            </Typography>
          </Stack>
          <Stack sx={{ pb: 5 }}>
            <Typography variant="h5" sx={styleSets.lineCamp(2)}>
              {currentProduct?.title}
            </Typography>
            <Stack alignItems="flex-end">
              <Button
                variant="text"
                href={generatePath(routerPages.ArtistMembership, {
                  artistId: membership.artist.id,
                })}
              >
                <Typography variant="subtitle1" color="primary.main">
                  {t('seeOtherPlans')}
                </Typography>
              </Button>
            </Stack>
            <Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="subtitle1">
                  {t(`${currentProduct?.plan?.interval}Fee`)}
                </Typography>
                <Typography variant="body2">
                  {t('amount', {
                    amount: currentProduct?.plan?.formattedAmount,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="subtitle1">{t('updateDate')}</Typography>
                <Typography variant="body2">
                  {membership?.formattedUpdatedAt}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="subtitle1">
                  {t('paymentMethod')}
                </Typography>
                <Typography variant="body2">
                  {t(membership?.paymentMethod || PaymentMethod.CreditCard)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Divider sx={{ borderColor: 'grey.200' }} />
          <Stack sx={{ py: 5 }}>
            <Typography variant="body2">{currentProduct?.content}</Typography>
          </Stack>
          <Divider sx={{ borderColor: 'grey.200' }} />
          <Stack flex={1} sx={{ py: 5 }} justifyContent="center">
            {membershipState === 'ACTIVE' && (
              <Typography variant="subtitle2" align="left">
                {t(
                  `${
                    membership?.paymentMethod || PaymentMethod.CreditCard
                  }AutoUpdate${monthSuffix}`,
                  {
                    count: month,
                    expiredAt: membership?.formattedExpiredAt,
                  }
                )}
              </Typography>
            )}
            {membershipState === 'CHANGED_PLAN' && (
              <Typography variant="subtitle2" align="left" color="text.hint">
                {t('cancelChangeNote', {
                  productTitle: downgradeProduct?.title,
                  expiredAt: membership?.formattedExpiredAt,
                })}
              </Typography>
            )}
            {membershipState === 'KONBINI_WAITING_PAYMENT' && (
              <Typography variant="subtitle2" align="left" color="text.hint">
                {t('konbiniJoinedNote')}
              </Typography>
            )}
            {membershipState === 'RESUME' && (
              <Typography variant="subtitle2" align="left" color="error.main">
                {t('cancelNote', {
                  expiredAt: membership?.formattedExpiredAt,
                })}
              </Typography>
            )}
          </Stack>
          <Stack flex={1} alignItems="center" sx={{ pt: 2 }} spacing={2}>
            {membershipState === 'KONBINI_WAITING_PAYMENT' && (
              <Stack alignItems="center">
                <Stack direction="row" spacing={1}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('paymentDeadlineKonbini')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {
                      membership?.artist?.pendingPurchasePatron
                        ?.formattedExpiredAt
                    }
                  </Typography>
                </Stack>
                <Button variant="text" onClick={onClick}>
                  {t('confirmPayment')}
                </Button>
              </Stack>
            )}
            {membershipState === 'RESUME' && (
              <ResumePlanButton
                type={membership.type}
                artistId={membership.artistId}
                resumingSubscription={resumingSubscription}
                onResumeSubscription={onResumeSubscription}
              />
            )}
            {membershipState === 'CHANGED_PLAN' && !isKonbini && (
              <CancelChangePlanButton
                type={membership.type}
                artistId={membership.artistId}
                cancellingChangeSubscription={cancellingChangeSubscription}
                onCancelChangeSubscription={onCancelChangeSubscription}
              />
            )}
            {membershipState === 'ACTIVE' &&
              (isKonbini ? (
                /**
                 * 支払い方法変更への導線
                 * NOTE:
                 * 支払い方法変更APIについて確認中のため、導線を表示していない
                 * https://app.clickup.com/t/908127/UT-15353
                 */
                false && (
                  <Button
                    size="large"
                    color="inherit"
                    sx={{
                      width: 240,
                      '&.MuiButtonBase-root:hover': { bgcolor: 'grey.50' },
                      bgcolor: 'grey.50',
                    }}
                    href={generatePath(
                      routerPages.ArtistMembershipPaymentEdit,
                      {
                        artistId: membership.artist.id ?? '#',
                        productId: currentProduct?.productId ?? '#',
                      }
                    )}
                  >
                    {t('changePaymentMethod')}
                  </Button>
                )
              ) : (
                <>
                  {
                    <ChangePlanButton
                      type={membership.type}
                      artistId={membership.artistId}
                      diffAmountSubscription={diffAmountSubscription}
                      formattedExpiredAt={membership.formattedExpiredAt ?? ''}
                      currentPlan={currentProduct?.plan}
                      upgradePlan={upgradePlan}
                      downgradePlan={downgradePlan}
                      onPreviewSubscriptionUpdate={onPreviewSubscriptionUpdate}
                      changingSubscription={changingSubscription}
                      onUpgradeSubscriptionPlan={onUpgradeSubscriptionPlan}
                      onDowngradeSubscriptionPlan={onDowngradeSubscriptionPlan}
                    />
                  }
                  {/**
                   * @description
                   * 決済方法によって振る舞いを変える
                   * stripe: stripe経由でキャンセル
                   * iap: ios設定から解約を促す
                   * google pay: アプリストアから解約を促す
                   */}
                  <CancelPlanButton
                    type={membership.type}
                    artistId={membership.artistId}
                    stoppingSubscription={stoppingSubscription}
                    onStopSubscription={onStopSubscription}
                  />
                  {membership?.iapSubscription?.store === 'app_store' && (
                    <Typography variant="body2">{t('cancelAtIOS')}</Typography>
                  )}
                  {membership?.iapSubscription?.store === 'play_store' && (
                    <Typography variant="body2">
                      {t('cancelAtAndroid')}
                    </Typography>
                  )}
                </>
              ))}
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

/**
 * @todo
 * 別タスクでスケルトンローディング実装
 */
const LoadingCell: Cell = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '90dvh',
      }}
    >
      <Loading />
    </Box>
  )
}
